/**
 * 前端监控事件上报核心：采样、脱敏、POST 到 /api/v1/system/monitoring/events。
 *
 * 环境变量：REACT_APP_MONITORING_ENABLED、REACT_APP_MONITORING_SAMPLE_RATE、REACT_APP_MONITORING_ENDPOINT。
 */
import type { MonitoringEventPayload } from './types';
import { getUserAccessToken } from '@/services/auth';

type MonitoringConfig = {
  endpoint: string;
  enabled: boolean;
  sampleRate: number;
  env: string;
  consoleOnly: boolean;
};

const SENSITIVE_PATTERNS = [
  /(1\d{2})\d{4}(\d{4})/g,
  /([A-Za-z0-9_-]{6})[A-Za-z0-9_-]{6,}/g,
];

let config: MonitoringConfig = {
  endpoint:
    process.env.REACT_APP_MONITORING_ENDPOINT ||
    '/api/v1/system/monitoring/events',
  enabled: process.env.REACT_APP_MONITORING_ENABLED !== 'false',
  sampleRate: Number(process.env.REACT_APP_MONITORING_SAMPLE_RATE || '0.2'),
  env: process.env.NODE_ENV || 'development',
  consoleOnly: process.env.NODE_ENV !== 'production',
};

function sanitizeText(value?: string) {
  if (!value) {
    return value;
  }

  return SENSITIVE_PATTERNS.reduce((current, pattern) => {
    return current.replace(pattern, '$1****$2');
  }, value);
}

function sanitizeValue(value: unknown): unknown {
  if (typeof value === 'string') {
    return sanitizeText(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      if (/token|mobile|phone|idcard|身份证|验证码|code/i.test(key)) {
        result[key] = typeof item === 'string' ? sanitizeText(item) : '[masked]';
        continue;
      }
      result[key] = sanitizeValue(item);
    }
    return result;
  }

  return value;
}

function shouldReport(event: MonitoringEventPayload) {
  if (!config.enabled) {
    return false;
  }

  if (event.eventType === 'page_view' || event.eventType === 'user_action') {
    return true;
  }

  if (config.sampleRate >= 1) {
    return true;
  }

  return Math.random() <= config.sampleRate;
}

export function configureMonitoring(nextConfig: Partial<MonitoringConfig>) {
  config = {
    ...config,
    ...nextConfig,
  };
}

export async function reportMonitoringEvent(
  payload: Omit<MonitoringEventPayload, 'app' | 'env' | 'clientTime'>,
) {
  const event: MonitoringEventPayload = {
    app: 'mvl-system-ui',
    env: config.env,
    clientTime: new Date().toISOString(),
    ...payload,
    message: sanitizeText(payload.message),
    stack: sanitizeText(payload.stack),
    extra: sanitizeValue(payload.extra) as Record<string, unknown> | undefined,
  };

  if (config.consoleOnly) {
    console.info('[monitoring]', event);
  }

  if (!shouldReport(event)) {
    return;
  }

  try {
    const accessToken = getUserAccessToken();
    await fetch(config.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(event),
      keepalive: true,
    });
  } catch (error) {
    console.warn('[monitoring] failed to report event', error);
  }
}
